import React, { useEffect, useRef, useState } from 'react';
import { EditableField } from '@/components/EditableField';
import styled from 'styled-components';
import { TrevorComponent } from '@trevor/ui-components';
import { useSavePublicDatasetMetadataMutation } from '@/features/publicDatasets/publicDatasetApi';
import { UUID } from '@/types';
import { SharedRowRequestMetadata } from '@/features/publicDatasets/types';
import { useSelector } from 'react-redux';
import { ResetQueryButton } from '@/features/publicDatasets/ResetQueryButton';
import Layer from '@/components/layers/Layer';
import { getResetQueryValidationValues } from '@/features/publicDatasets/selectors';

type PublicDatasetQueryNameProps = {
  rowRequestId: UUID;
  datasourceId: UUID;
  initialRowRequest: Nullable<string>;
};

const PublicDatasetQueryName: TrevorComponent<PublicDatasetQueryNameProps> = ({
  rowRequestId,
  datasourceId,
  className,
  initialRowRequest,
}) => {
  const { metadata, layers, selectedLayerId, metadataChanged } = useSelector(
    getResetQueryValidationValues,
  );

  const [titleEditing, setTitleEditing] = useState<boolean>();
  const [datasetHasChanges, setDatasetHasChanges] = useState(false);
  // we use this ref to track the layers changes. It contains the previous value
  const previousLayer = useRef<Layer[]>([]);
  const [update] = useSavePublicDatasetMetadataMutation();

  const onResetQuery = () => {
    setDatasetHasChanges(false);
    previousLayer.current = [];
  };

  const getMetadata = (): SharedRowRequestMetadata => {
    return {
      currentMetadataId: metadata?.currentMetadataId,
      datasourceId,
      title: metadata?.title,
      description: metadata?.description,
      rowRequestId,
      chartingOptions: metadata?.chartingOptions,
    };
  };

  const updateMetadata = (meta: SharedRowRequestMetadata) => {
    if (rowRequestId) {
      update(meta);
    }
  };

  const updateTitle = (title: string) => {
    updateMetadata({
      ...getMetadata(),
      title,
    });
  };

  const updateDescription = (description: string) => {
    updateMetadata({
      ...getMetadata(),
      description,
    });
  };

  useEffect(() => {
    // initially layers is always an empty array, it gets the first values after the page is loaded
    if (
      initialRowRequest &&
      previousLayer.current.length > 0 &&
      layers.length > 0
    ) {
      setDatasetHasChanges(true);
      return;
    }
    previousLayer.current = layers;
  }, [layers, selectedLayerId]);

  return (
    <div className={className}>
      <TitleWrapper>
        <NameEditableField
          placeholder="Unnamed"
          handleInput={updateTitle}
          value={metadata?.title || ''}
          onEditStateChange={setTitleEditing}
          displayEditIcon
        />
        {initialRowRequest &&
          (datasetHasChanges || metadataChanged) &&
          !titleEditing && (
            <ResetQueryButton
              initialRowRequest={initialRowRequest}
              onReset={onResetQuery}
              datasourceId={datasourceId}
            />
          )}
      </TitleWrapper>
      <DescriptionEditableField
        placeholder="Description"
        handleInput={updateDescription}
        value={metadata?.description || ''}
        tabIndex={0}
        activateOnFocus
      />
    </div>
  );
};

const StyledEditableField = styled(EditableField)`
  --v-height: 29px;

  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: var(--v-height);
  height: var(--v-height);
  cursor: pointer;

  color: #061e2d;

  input {
    height: var(--v-height);
    width: 800px;
    background: transparent;
    border: none;
    outline: none;
    padding: 0;
  }
`;

const NameEditableField = styled(StyledEditableField)`
  font-weight: 700;
  font-size: 18px;
  --icon-size: 13px;

  input {
    width: 800px;
  }

  svg {
    width: var(--icon-size);
    height: var(--icon-size);
    margin-left: 9px;
  }
`;
const DescriptionEditableField = styled(StyledEditableField)`
  font-weight: 400;
  font-size: 14px;

  input {
    width: 900px;
  }
`;

export default styled(PublicDatasetQueryName)`
  padding-top: 30px;
  padding-left: 33px;
  width: fit-content;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 8px;
  height: 44px;
`;
